import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getCountFromServer, query, where } from 'firebase/firestore';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ClipboardList,
  Users,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useUnitContext } from '@/contexts/UnitContext';

function formatPence(pence: number | undefined | null): string {
  if (typeof pence !== 'number') return '£0.00';
  return `£${(pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function TreasurerDashboardPage() {
  const { activeUnit, treasurerSettings, isTreasurer, isWorshipfulMaster, isAuditor } = useUnitContext();

  const [memberCount, setMemberCount] = useState<number | null>(null);
  useEffect(() => {
    if (!activeUnit) return;
    let cancelled = false;
    (async () => {
      try {
        const q = query(
          collection(db, 'units', activeUnit.unitId, 'treasurerMembers'),
          where('isArchived', '==', false),
        );
        const c = await getCountFromServer(q);
        if (!cancelled) setMemberCount(c.data().count);
      } catch (err) {
        console.error('Failed to count members', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeUnit]);

  if (!activeUnit) return null;

  const today = new Date();
  const upcomingMeeting = (treasurerSettings?.meetingDates ?? [])
    .map((d) => ({ date: new Date(d), iso: d }))
    .filter((m) => !Number.isNaN(m.date.getTime()) && m.date.getTime() >= today.setHours(0, 0, 0, 0))
    .sort((a, b) => a.date.getTime() - b.date.getTime())[0];

  const daysUntilMeeting = upcomingMeeting
    ? Math.ceil((upcomingMeeting.date.getTime() - new Date().setHours(0, 0, 0, 0)) / 86_400_000)
    : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-500 text-sm mt-1">
          {activeUnit.name} No. {activeUnit.number}
          {' · '}
          <RoleSummary
            isTreasurer={isTreasurer}
            isWorshipfulMaster={isWorshipfulMaster}
            isAuditor={isAuditor}
          />
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card
          title="Active members"
          value={memberCount === null ? '—' : memberCount.toLocaleString('en-GB')}
          icon={<Users className="h-5 w-5 text-emerald-600" />}
          link={`/u/${activeUnit.unitId}/members`}
          linkLabel="Manage members"
        />
        <Card
          title="Capitation rate"
          value={formatPence(treasurerSettings?.grandLodgeCapitationRatePence)}
          subtitle="per member, per year"
          icon={<ClipboardList className="h-5 w-5 text-emerald-600" />}
          link={`/u/${activeUnit.unitId}/settings`}
          linkLabel={isTreasurer ? 'Edit settings' : undefined}
        />
        <Card
          title="Next meeting"
          value={upcomingMeeting ? upcomingMeeting.iso : '—'}
          subtitle={
            daysUntilMeeting !== null
              ? daysUntilMeeting === 0
                ? 'Today'
                : `${daysUntilMeeting} day${daysUntilMeeting === 1 ? '' : 's'} away`
              : 'No upcoming meeting set'
          }
          icon={<Calendar className="h-5 w-5 text-emerald-600" />}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Foundation complete
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            The Treasurer module is set up for this lodge. The data model,
            security rules, member management, fund/category management and
            settings are in place.
          </p>
          <ul className="text-sm text-slate-700 space-y-1.5">
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Members CRUD
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Funds and categories
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Audit log
            </li>
            <li className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Default categorisation rules seeded
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> Coming next
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            Subsequent build stages will add Open Banking sync, the rules
            engine, subscriptions, full reporting, and email automation.
          </p>
          <div className="text-sm text-slate-700 space-y-1.5">
            <Step n={2} label="Open Banking sync + manual journal entries" />
            <Step n={3} label="Categorisation rules engine + Needs Review queue" />
            <Step n={4} label="Subscriptions year + arrears tracking" />
            <Step n={5} label="Reports (meeting, annual, audit, charity, GL return)" />
            <Step n={6} label="Email automation (reminders, alerts, report packs)" />
            <Step n={7} label="Full dashboard polish + end-to-end tests" />
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({
  title,
  value,
  subtitle,
  icon,
  link,
  linkLabel,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  link?: string;
  linkLabel?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 text-xs font-medium uppercase text-slate-500">
        {icon}
        {title}
      </div>
      <div className="mt-3 text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
      {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
      {link && linkLabel && (
        <Link
          to={link}
          className="mt-3 inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline"
        >
          {linkLabel} <ArrowRight className="h-3 w-3" />
        </Link>
      )}
    </div>
  );
}

function Step({ n, label }: { n: number; label: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className="h-5 w-5 inline-flex items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">
        {n}
      </span>
      <span>{label}</span>
    </div>
  );
}

function RoleSummary({
  isTreasurer,
  isWorshipfulMaster,
  isAuditor,
}: {
  isTreasurer: boolean;
  isWorshipfulMaster: boolean;
  isAuditor: boolean;
}) {
  const roles: string[] = [];
  if (isTreasurer) roles.push('Treasurer');
  if (isWorshipfulMaster) roles.push('Worshipful Master');
  if (isAuditor) roles.push('Auditor');
  return <span>You are signed in as {roles.length ? roles.join(', ') : 'a member with no treasurer role'}</span>;
}
